import { useCallback, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { useIntl } from 'react-intl';

import { CreateSupportCaseDocument } from 'generated/rbi-graphql';
import { getErrorDialogForGraphQLCode, parseGraphQLErrorCodes } from 'utils/errors';
import logger from 'utils/logger';

import { CreateCaseState, ICreateSupportCaseInput, IUseKnowledgeForceCaseCreation } from '../types';

export const useKnowledgeForceCaseCreation = (): IUseKnowledgeForceCaseCreation => {
  const [caseReferenceNumber, setCaseReferenceNumber] = useState<string | null>(null);
  const [createCaseState, setCreateCaseState] = useState<CreateCaseState>(CreateCaseState.DEFAULT);
  const [createCaseError, setCreateCaseError] = useState('');
  const [executeCreateKnowledgeForceSupportCase] = useMutation(CreateSupportCaseDocument);
  const { formatMessage } = useIntl();

  const createSupportCase = useCallback(
    async (caseInfo: ICreateSupportCaseInput) => {
      setCreateCaseState(CreateCaseState.LOADING);
      try {
        const { data } = await executeCreateKnowledgeForceSupportCase({
          variables: { caseInfo },
        });
        const { caseId } = data.createSupportCase;

        setCaseReferenceNumber(caseId);
        setCreateCaseState(CreateCaseState.SUCCESS);
        return caseId;
      } catch (error) {
        const [parsedApolloError] = parseGraphQLErrorCodes(error);

        if (parsedApolloError) {
          const mappedErrorMessage = getErrorDialogForGraphQLCode(parsedApolloError, formatMessage);
          if (mappedErrorMessage) {
            setCreateCaseError(mappedErrorMessage.message);
          }
        }

        logger.error(`Failed to create knowledge force support case: ${error.message}`);
        setCreateCaseState(CreateCaseState.ERROR);
        return null;
      }
    },
    [executeCreateKnowledgeForceSupportCase, formatMessage]
  );

  return {
    caseReferenceNumber,
    createCaseState,
    createSupportCase,
    createCaseError,
  };
};
